<template>
    <v-card
        class="product-list-item pb-4 d-flex flex-column flex-grow-1 h-100"
        elevation="1"
        itemtype="https://schema.org/Product"
        itemscope
    >
        <div
            class="d-none"
            itemtype="https://schema.org/Brand"
            itemprop="brand"
            itemscope
        >
            <meta itemprop="name" :content="brandTitle" />
        </div>

        <ProductFlags
            v-if="item.flags"
            class="card-top-product-flags"
            v-model="item.flags"
            density="compact"
            size="large"
            label
        ></ProductFlags>

        <NuxtLink
            :to="itemUrl"
            class="d-block"
            :class="{ 'pa-4': isCutoutImage }"
        >
            <FullImage
                class="w-100 d-table"
                :key="`prod-img-id-${selectedSku?.defaultImage?.uid || useId()}`"
                :image-object="selectedSku?.defaultImage"
                :type="['small', 'medium']"
            ></FullImage>
        </NuxtLink>

        <div
            class="position-relative d-flex flex-column flex-grow-1"
        >

            <v-divider
                class="mb-4"
            ></v-divider>

            <DebugContent>
                <details
                    class="mb-2"
                >
                    <summary
                        class="cursor-pointer"
                    >
                        Options Debug Info
                    </summary>
                    <div>
                        <h5
                            class="py-0 my-0"
                        >
                            Model
                        </h5>
                        <ul>
                            <li
                                v-for="(v, k) in optionsModel"
                                :key="`optionsModel-opt-${k}`"
                            >
                                <strong>{{ k }}:</strong>
                                {{ v }}
                            </li>
                        </ul>

                        <h5
                            class="py-0 my-0"
                        >
                            Options List
                        </h5>
                        <ul>
                            <li
                                v-for="(opt, i) in optionsList"
                                :key="`optionsList-opt-${i}`"
                                :class="{
                                    'text-success': opt.showOnCollectionPages
                                }"
                            >
                                {{ opt.attribute.uid }}
                            </li>
                        </ul>

                        <h5
                            class="py-0 my-0"
                        >
                            Disabled Model
                        </h5>
                        <ul>
                            <li
                                v-for="(opt, i) in optionsDisabledModel"
                                :key="`optionsDisabledModel-opt-${i}`"
                            >
                                {{ opt }}
                            </li>
                        </ul>
                    </div>
                </details>
            </DebugContent>

            <!-- Options -->
            <ProductsListOptions
                class="px-4"
                :options-model="optionsModel"
                :options-list="optionsList"
                :options-disabled-model="optionsDisabledModel"
                @on-change="onOptionChange"
                hide-label
            ></ProductsListOptions>

            <NuxtLink
                :to="itemUrl"
                class="text-decoration-none d-flex flex-column mt-auto px-4"
            >
                <!-- Item Title -->

                <div
                    class="product-list-item-title mt-4 mb-2"
                >
                    <ProductTitle
                        :product="item"
                        :compact="true"
                        :font-size-px="20"
                        natural-case
                        no-bold
                    ></ProductTitle>
                    <ProductRatingTitle
                        class="mt-1"
                        :average="item.averageRating"
                        :total="item.reviewCount"
                    ></ProductRatingTitle>

                    <meta itemprop="description" :content="item.description" />
                </div>

                <!-- Price -->
                <div
                    v-if="selectedSku"
                    class="mt-1 d-flex flex-column"
                >
                    <span
                        v-if="currentMinPrice"
                        class="v-align-middle"
                    >
                        <span class="text-medium-emphasis">from</span> <span class="font-weight-medium">{{ formatCurrency(currentMinPrice) }}</span>
                    </span>
                    <ProductPriceInfo
                        v-else
                        class="d-flex flex-column"
                        :current-sku="selectedSku"
                        size="sm"
                        order-reverse
                        no-bold
                    ></ProductPriceInfo>
                </div>
            </NuxtLink>

        </div>
    </v-card>
</template>

<script lang="ts" setup>
    import type {
        Product,
        Sku,
        SkusMap,
        ProductAttrsMaps,
        ProductAttrsGroupOption,
        ProductOptionsModel,
        ProductAttrsDisabledModel
    } from '~/types';

    import {
        useSkusUtils,
        useProductAttrs,
    } from '~/composables';
    import {
        useCustomNuxtUtils
    } from 'x1-base/composables';

    import { pathJoin, formatCurrency } from 'x1-base/utils';
    import ProductTitle from '~/components/products/product/ProductTitle/ProductTitle.vue';
    import ProductRatingTitle from '~/components/products/ProductRatingTitle/ProductRatingTitle.vue';
    import ProductFlags from '~/components/products/ProductFlags/ProductFlags.vue';
    import FullImage from '~/components/media/FullImage/FullImage.vue';
    import ProductPriceInfo from '~/components/products/ProductPriceInfo/ProductPriceInfo.vue';

    import ProductsListOptions from '~/components/products/products-list/ProductsListOptions/ProductsListOptions.vue';
    import DebugContent from '~/components/DebugContent/DebugContent.vue';
    import { isUndefined as lo_isUndefined } from 'es-toolkit';
    import {
        ProductListItemStateKeyPrefix
    } from '~/constants';

    const props = withDefaults(
        defineProps<{
            item: Product;
            selectFirst: boolean;
            brandTitle: string;
            pageKey: string;
        }>(),
        {
            selectFirst: false
        }
    );

    const {
        getSkusMap,
        getSkuByUid,
        getValuesModelBySku,
        findFirstSkuByPartialOptions
    } = useSkusUtils();

    const {
        processAttrsGroups
    } = useProductAttrs();

    const {
        removeOnceKey
    } = useCustomNuxtUtils();

    const debugConsole = useDebug();

    const {
        skus,
        optionGroups,
        defaultSkuUid,
        uid: itemUid
    } = props.item;


    const selectedSku = useState<Nilish<Sku>>(`${ProductListItemStateKeyPrefix}SelectedSku_${itemUid}`, () => null);
    const itemUrl = computed<string>(() => pathJoin('/', selectedSku.value?.path || ''));
    // Transparent cutout images (the only ones that get a drop shadow) read
    // better inset from the card edges; opaque photos run full-bleed.
    const isCutoutImage = computed<boolean>(() => !!selectedSku.value?.defaultImage?.showDropShadow);
    const skusMap = useState<Nilish<SkusMap>>(`${ProductListItemStateKeyPrefix}SkusMap_${itemUid}`, () => null);
    const currentMinPrice = computed<Nullable<number>>(() => !lo_isUndefined(props.item?.minPrice) ? props.item.minPrice : null);

    const attrsMapOptions = useState<Nilish<ProductAttrsMaps>>(`${ProductListItemStateKeyPrefix}AttrsMapOpts_${itemUid}`, () => null);
    const optionsDisabledModel = ref<ProductAttrsDisabledModel>({});
    // const optionsInvalidValuesCombos = useState<string[][]>('keyProdListFormOptInvValCombos', () => []);

    const optionsList = ref<ProductAttrsGroupOption[]>(optionGroups);
    const optionsModel = useState<ProductOptionsModel>(`${ProductListItemStateKeyPrefix}OptsModel_${itemUid}`, () => ({}));
    const attrsWhitelist = ref<string[]>([]);

    const onceKey = `${props.pageKey}_${itemUid}`;

    callOnce(onceKey, () => {
        skusMap.value = getSkusMap(skus);
        attrsMapOptions.value = processAttrsGroups(optionGroups);
        attrsWhitelist.value = optionGroups.reduce((acc: string[], opt) => {
            if (opt.showOnCollectionPages) acc.push(opt.attribute.uid);
            return acc;
        }, []);


        selectedSku.value = getSkuByUid(skus, defaultSkuUid) || skus[0];
        const skuUid = selectedSku.value?.uid;

        if (skuUid) {
            optionsModel.value = getValuesModelBySku(skusMap.value, attrsMapOptions.value.attrs, skuUid);
        }
    }, { mode: 'navigation' });

    function onOptionChange(updatedModel: ProductOptionsModel): void {
        const model = attrsWhitelist.value.reduce((acc: Record<string, string>, uid: string) => {
            if (updatedModel[uid]) acc[uid] = updatedModel[uid];
            return acc;
        }, {});

        const sku = findFirstSkuByPartialOptions(skus, model);

        if (sku) {
            selectedSku.value = sku;
            return;
        }

        debugConsole.warn('Select Sku by partial props not found... use default (or first) sku');
        selectedSku.value = getSkuByUid(skus, props.item.defaultSkuUid) || skus[0];
    }

    onMounted(() => {
        removeOnceKey(onceKey);
    });

    onBeforeUnmount(() => {
        removeOnceKey(onceKey);
    });
</script>

<style lang="scss">
.product-list-item {
    // The theme maps font-weight-regular to 500 (vuetify.scss) and the
    // font-size-6 utility pins 20px, so taglines render exactly like the
    // natural-case title. Force true regular at 16px here.
    .product-title h4.font-weight-regular {
        font-weight: 400 !important;
        font-size: 16px !important;
    }
}
</style>
